/**
 * Схемы полей "Ключи интеграции" по протоколам процессинга.
 *
 * Каждое поле имеет:
 *   key       — ключ внутри terminal.data[source]
 *   label     — лейбл для админа (русский)
 *   source    — куда сохранять/откуда читать в terminal.data:
 *                 'processingAuth' (по умолчанию) — terminal.data.processingAuth[key]
 *                 'nodaBank' — terminal.data.nodaBank[key] (UnlimcoBank фасад)
 *   multiline — textarea вместо input (для PEM-ключей)
 *   placeholder — подсказка
 *   hint      — пояснение под полем
 *
 * Если protocol не в этом списке — рендерим старый flat-вью processingAuth как есть.
 */

const PROCESSING_AUTH = 'processingAuth';
const NODA_BANK = 'nodaBank';

export const AUTH_SCHEMAS = {
    TransCapitalBank: [
        { key: 'login', label: 'Логин', source: PROCESSING_AUTH },
        { key: 'secret', label: 'Секретный ключ', source: PROCESSING_AUTH },
    ],

    Hydracode: [
        { key: 'baseUrl', label: 'Адрес API Hydracode', source: PROCESSING_AUTH, placeholder: 'https://...' },
        { key: 'apiKey', label: 'API ключ', source: PROCESSING_AUTH },
        { key: 'signingKey', label: 'Ключ подписи', source: PROCESSING_AUTH },
    ],

    PayCraft: [
        { key: 'baseUrl', label: 'Адрес API PayCraft', source: PROCESSING_AUTH, placeholder: 'https://...' },
        { key: 'apiKey', label: 'API ключ', source: PROCESSING_AUTH },
        { key: 'signingKey', label: 'Ключ подписи', source: PROCESSING_AUTH },
    ],

    FinStar: [
        { key: 'baseUrl', label: 'Адрес API FinStar', source: PROCESSING_AUTH, placeholder: 'https://...' },
        { key: 'apiKey', label: 'API ключ', source: PROCESSING_AUTH },
        { key: 'signingKey', label: 'Ключ подписи', source: PROCESSING_AUTH },
    ],

    NodaBank: [
        { key: 'baseUrl', label: 'Адрес API NodaBank', source: NODA_BANK, placeholder: 'https://payservice.nodabank.ru' },
        { key: 'partnerId', label: 'ID партнёра', source: NODA_BANK, placeholder: 'unlimco001' },
        { key: 'secret', label: 'Секрет API', source: NODA_BANK },
        {
            key: 'publicKey',
            label: 'Публичный ключ NodaBank (PEM)',
            source: NODA_BANK,
            multiline: true,
            placeholder: '-----BEGIN PUBLIC KEY-----\n...',
        },
        {
            key: 'privateKey',
            label: 'Наш приватный ключ (PEM)',
            source: NODA_BANK,
            multiline: true,
            placeholder: '-----BEGIN PRIVATE KEY-----\n...',
        },
        {
            key: 'account',
            label: 'Расчётный счёт NodaBank',
            source: NODA_BANK,
            hint: 'Куда NodaBank нам зачисляет деньги',
        },
    ],

    // UnlimcoBank — фасад: карты идут через NodaBank, СБП через PSB B2C.
    // Поэтому показываем И NodaBank-поля, И PSB-СБП-поля. Все хранятся в processingAuth кроме nodaBank-секции.
    UnlimcoBank: [
        { section: 'NodaBank (выплаты на карты)' },
        { key: 'baseUrl', label: 'Адрес API NodaBank', source: NODA_BANK, placeholder: 'https://payservice.nodabank.ru' },
        { key: 'partnerId', label: 'ID партнёра', source: NODA_BANK, placeholder: 'unlimco001' },
        { key: 'secret', label: 'Секрет API', source: NODA_BANK },
        {
            key: 'publicKey',
            label: 'Публичный ключ NodaBank (PEM)',
            source: NODA_BANK,
            multiline: true,
            placeholder: '-----BEGIN PUBLIC KEY-----\n...',
        },
        {
            key: 'privateKey',
            label: 'Наш приватный ключ (PEM)',
            source: NODA_BANK,
            multiline: true,
            placeholder: '-----BEGIN PRIVATE KEY-----\n...',
        },
        {
            key: 'account',
            label: 'Расчётный счёт NodaBank',
            source: NODA_BANK,
            hint: 'Куда NodaBank нам зачисляет деньги',
        },

        { section: 'ПСБ (выплаты по СБП)' },
        { key: 'sbp_id', label: 'ID СБП (LB...)', source: PROCESSING_AUTH, placeholder: 'LB0000005837' },
        { key: 'merchant', label: 'ID мерчанта ПСБ', source: PROCESSING_AUTH },
        { key: 'terminal', label: 'Терминал ПСБ', source: PROCESSING_AUTH },
        { key: 'sbp_account_number', label: 'Счёт СБП в ПСБ', source: PROCESSING_AUTH },
        { key: 'filial_bic', label: 'БИК филиала ПСБ', source: PROCESSING_AUTH },
        { key: 'merch_inn', label: 'ИНН мерчанта', source: PROCESSING_AUTH },
        { key: 'merch_name', label: 'Название мерчанта (для ПСБ)', source: PROCESSING_AUTH },
        { key: 'merch_city', label: 'Город мерчанта', source: PROCESSING_AUTH },
        { key: 'key_component_1', label: 'Компонент ключа 1', source: PROCESSING_AUTH },
        { key: 'key_component_2', label: 'Компонент ключа 2', source: PROCESSING_AUTH },
        {
            key: 'rsa_private_key_pem',
            label: 'Наш приватный RSA-ключ (PEM)',
            source: PROCESSING_AUTH,
            multiline: true,
            placeholder: '-----BEGIN RSA PRIVATE KEY-----\n...',
        },
        {
            key: 'rsa_psb_public_key_pem',
            label: 'Публичный RSA-ключ ПСБ (PEM)',
            source: PROCESSING_AUTH,
            multiline: true,
            placeholder: '-----BEGIN PUBLIC KEY-----\n...',
        },
        // payment_receiver_*_name и payment_sender_account_number в схеме НЕ показываем.
        // Backend требует ФИО получателя в каждом payload выплаты. Дефолты на уровне
        // терминала — критическая дыра: при отсутствии ФИО в payload деньги бы ушли
        // условному default-получателю.
    ],

    Psb: [
        { section: 'ПСБ карточная интеграция' },
        { key: 'cpid', label: 'CPID', source: PROCESSING_AUTH, hint: 'Договор интернет-эквайринга' },
        { key: 'cps', label: 'CPS (терминал)', source: PROCESSING_AUTH },
        { key: 'email', label: 'Email кабинета', source: PROCESSING_AUTH },
        { key: 'password', label: 'Пароль кабинета', source: PROCESSING_AUTH },
        { key: 'secret', label: 'Секрет подписи', source: PROCESSING_AUTH },
    ],
};

/**
 * Возвращает список полей для протокола. Если протокол неизвестен — null
 * (UI откатывается на старый flat-вью processingAuth).
 */
export function getAuthSchema(processingProtocol) {
    if (!processingProtocol) return null;
    return AUTH_SCHEMAS[processingProtocol] || null;
}

export const SOURCE_PROCESSING_AUTH = PROCESSING_AUTH;
export const SOURCE_NODA_BANK = NODA_BANK;



// WEBPACK FOOTER //
// ./src/Components/ComponentsOfAdministrator/TerminalEditing/authSchemas.js